import {defineStore} from 'pinia';
import {type Ref, ref} from 'vue';

import {DashboardHomeApi} from '@/shared/features/dashboardHome/dashboardHome.api';
import type {DashboardHomeLink, DashboardHomeModel} from '@/shared/features/dashboardHome/dashboardHome.model';
import {DashboardInfoApi} from '@/shared/features/dashboardInfo/dashboardInfo.api';
import type {BannersModel, NavigationModel} from '@/shared/features/dashboardInfo/dashboardInfo.model';
import type {DashboardInfoResponse} from '@/shared/features/dashboardInfo/dashboardInfo.requests';

export const useDashboardStore = defineStore(
   'dashboard',
   () => {
      const dashboardData: Ref<DashboardInfoResponse | null> = ref(null);
      const dashboardNavigation: Ref<NavigationModel[] | null> = ref(null);
      const bannersData: Ref<BannersModel | null> = ref(null);
      const infoLoaded = ref<boolean>(false);

      const links = ref<DashboardHomeLink|null>(null);
      const homeTitle = ref<string>('');
      const linksLoaded = ref<boolean>(false);

      const initData = async (hard_load: boolean|null) => {
         if (infoLoaded.value && hard_load !== true) return;

         const cached = sessionStorage.getItem('nav_data');
         if (cached) {
            const cachedData: DashboardInfoResponse = JSON.parse(cached);
            setDashboardData(cachedData);
            infoLoaded.value = true;
            return cachedData;
         }

         return loadFreshData();
      };

      const loadFreshData = async () => {
         try {
            const response: DashboardInfoResponse = await DashboardInfoApi.get();

            setDashboardData(response, true);
            infoLoaded.value = true;

            return dashboardData;
         } catch (error) {
            throw error;
         }
      };

      const reloadNavigationData = () => {
         DashboardInfoApi.getNavigation().then((response) => {
            setNavigationData(response.data);
         });
      };

      const reloadBannersData = () => {
         DashboardInfoApi.getBanners().then((response) => {
            setBannersData(response.data);
         });
      };

      const setDashboardData = (data: DashboardInfoResponse | null, store?: boolean) => {
         dashboardData.value = data;
         dashboardNavigation.value = data?.navigation ?? null;
         bannersData.value = data?.banners ?? null;

         if(store === true) {
            sessionStorage.setItem('nav_data', JSON.stringify(data));
         }
      };

      const setNavigationData = (navData: NavigationModel[] | null) => {
         const cached = sessionStorage.getItem('nav_data');
         let rawData: DashboardInfoResponse|null = dashboardData.value;
         if (cached) {
            rawData = JSON.parse(cached) as DashboardInfoResponse;
         }

         if(!rawData) {
            rawData = {};
         }

         rawData.navigation = navData;

         setDashboardData(rawData, true);
      };

      const setBannersData = (data: BannersModel | null) => {
         const cached = sessionStorage.getItem('nav_data');
         let rawData: DashboardInfoResponse|null = dashboardData.value;
         if (cached) {
            rawData = JSON.parse(cached) as DashboardInfoResponse;
         }

         if(!rawData) {
            rawData = {};
         }

         rawData.banners = data;

         setDashboardData(rawData, true);
      };

      const resetData = () => {
         setDashboardData(null);
         links.value = null;
         homeTitle.value = '';
         linksLoaded.value = false;
         sessionStorage.removeItem('home_data');
         sessionStorage.removeItem('nav_data');
      };

      const fetchLinks = async () => {
         if (linksLoaded.value) return;

         const cached = sessionStorage.getItem('home_data');
         if (cached) {
            const cachedData: DashboardHomeModel = JSON.parse(cached);
            links.value = cachedData.links;
            homeTitle.value = cachedData.title;
            linksLoaded.value = true;
            return;
         }

         DashboardHomeApi.get().then((response) => {
            links.value = response.links;
            homeTitle.value = response.title;
            linksLoaded.value = true;
            sessionStorage.setItem('home_data', JSON.stringify(response));
         });
      };

      return {
         dashboardData,
         dashboardNavigation,
         resetData,
         initData,
         bannersData,
         infoLoaded,
         links,
         linksLoaded,
         homeTitle,
         fetchLinks,
         setDashboardData,
         setNavigationData,
         setBannersData,
         reloadNavigationData,
         reloadBannersData,
         loadFreshData,
      };
   },
);
